import { useRef, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Loader2, Upload, X, ImageIcon, Film } from 'lucide-react';
import { toast } from 'sonner';

const MAX_IMAGE_MB = 25;
const MAX_VIDEO_MB = 100;

const isVideo = (mime) => (mime || '').startsWith('video/');

const buildUrl = (apiBaseUrl, mediaUrl) => {
  if (!mediaUrl) return '';
  if (mediaUrl.startsWith('http://') || mediaUrl.startsWith('https://')) return mediaUrl;
  // mediaUrl is like '/api/uploads/<filename>' — backend routes live under /api, keep it.
  return `${apiBaseUrl.replace(/\/$/, '')}${mediaUrl}`;
};

/**
 * Reusable media upload area — renders existing media + upload button.
 * Props:
 *   post: { id, media: [...] }
 *   onChange: fn(updatedMediaList)
 */
export const PostMediaUpload = ({ post, onChange }) => {
  const { api } = useAuth();
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [removing, setRemoving] = useState(null);
  const inputRef = useRef(null);

  const apiBase = process.env.REACT_APP_BACKEND_URL || '';
  const media = post?.media || [];

  const triggerPick = () => inputRef.current?.click();

  const handleFiles = async (files) => {
    if (!files || files.length === 0) return;
    if (!post?.id) {
      toast.error('Save the post first before uploading media');
      return;
    }
    for (const file of Array.from(files)) {
      const maxMb = file.type.startsWith('video/') ? MAX_VIDEO_MB : MAX_IMAGE_MB;
      if (file.size > maxMb * 1024 * 1024) {
        toast.error(`${file.name} exceeds ${maxMb} MB limit`);
        continue;
      }
      const fd = new FormData();
      fd.append('file', file);
      setUploading(true);
      setProgress(0);
      try {
        const res = await api.post(`/posts/${post.id}/media`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
          },
        });
        const newMedia = [...(post.media || []), res.data];
        onChange?.(newMedia);
        toast.success(`${file.name} uploaded`);
      } catch (error) {
        toast.error(error.response?.data?.detail || `Failed to upload ${file.name}`);
      } finally {
        setUploading(false);
        setProgress(0);
      }
    }
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleRemove = async (mediaId) => {
    setRemoving(mediaId);
    try {
      await api.delete(`/posts/${post.id}/media/${mediaId}`);
      onChange?.((post.media || []).filter((m) => m.id !== mediaId));
      toast.success('Removed');
    } catch {
      toast.error('Failed to remove');
    } finally {
      setRemoving(null);
    }
  };

  return (
    <div className="space-y-2" data-testid="post-media-upload">
      {media.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {media.map((m) => {
            const url = buildUrl(apiBase, m.url);
            return (
              <div
                key={m.id}
                className="relative rounded-lg overflow-hidden border border-zinc-200 bg-zinc-50 aspect-square group"
                data-testid={`media-item-${m.id}`}
              >
                {isVideo(m.mime) ? (
                  <video
                    src={url}
                    className="w-full h-full object-cover"
                    controls
                    preload="metadata"
                  />
                ) : (
                  <img src={url} alt={m.original_name} className="w-full h-full object-cover" />
                )}
                <div className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-black/60 text-white text-[10px] flex items-center gap-1">
                  {isVideo(m.mime) ? <Film className="w-2.5 h-2.5" /> : <ImageIcon className="w-2.5 h-2.5" />}
                  {Math.round(m.size / 1024)} KB
                </div>
                <button
                  type="button"
                  onClick={() => handleRemove(m.id)}
                  disabled={removing === m.id}
                  className="absolute top-1 right-1 w-5 h-5 rounded-full bg-black/70 hover:bg-red-600 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  data-testid={`remove-media-${m.id}`}
                  title="Remove"
                >
                  {removing === m.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <X className="w-3 h-3" />}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <input
        ref={inputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp,image/gif,video/mp4,video/quicktime,video/webm"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
        data-testid="media-file-input"
      />

      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={triggerPick}
        disabled={uploading}
        data-testid="upload-media-btn"
      >
        {uploading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            Uploading {progress}%
          </>
        ) : (
          <>
            <Upload className="w-4 h-4 mr-2" />
            Upload image or video
          </>
        )}
      </Button>
      <p className="text-[10px] text-zinc-500 text-center">
        Up to {MAX_IMAGE_MB} MB images · {MAX_VIDEO_MB} MB videos · PNG/JPG/WebP/GIF · MP4/MOV/WebM
      </p>
    </div>
  );
};
